import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import {
  countsTowardAnalytics,
  type RecordingProcessRow,
  type RecordingTenantRow
} from '@bliks-io/contracts';
import {
  Button,
  DataTable,
  createColumnHeader,
  Page,
  PageContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { useSuperAdminRecordingAnalytics } from '@/pages/admin/api/queries/useSuperAdminRecordingAnalytics';
import type { TFn } from '@/lib/i18n';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import { SummaryCard } from '@/pages/admin/components/SummaryCard';

const formatMinutes = (t: TFn, ms: number) =>
  t('recordingAnalytics.minutesSuffix', { value: (ms / 60_000).toFixed(1) });

/** Steps revealed when a process row is expanded, sorted desc by time. */
function StepsSubTable({ steps }: { steps: RecordingProcessRow['steps'] }) {
  const { t } = useTranslation();
  return (
    <div className="bg-muted/40 px-12 py-3">
      <div className="grid grid-cols-[1fr_auto] gap-x-6 text-sm">
        <span className="pb-1 font-medium text-muted-foreground">
          {t('recordingAnalytics.step')}
        </span>
        <span className="pb-1 text-right font-medium text-muted-foreground">
          {t('recordingAnalytics.stepTime')}
        </span>
        {steps.map((step) => (
          <Fragment key={step.recordingId}>
            <span className="py-0.5">{step.name}</span>
            <span className="py-0.5 text-right tabular-nums">
              {formatMinutes(t, step.activeRecordingMs)}
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export function AnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/admin/analytics/' });

  const { data: tenantsData } = useListTenants();
  // The backend excludes these tenant types from the analytics, so the scope
  // picker must not offer them either.
  const realTenants = tenantsData?.tenants?.filter((tenant) =>
    countsTowardAnalytics(tenant.tenantType)
  );
  const { data, isLoading, isPlaceholderData } = useSuperAdminRecordingAnalytics({
    tenantId: search.tenantId
  });

  const summary = data?.summary;
  const tenants = data?.tenants ?? [];
  const processes = data?.processes ?? [];

  const setTenant = useCallback(
    (tenantId?: string) => navigate({ to: '/admin/analytics', search: { tenantId } }),
    [navigate]
  );

  const selectedTenantName = realTenants?.find((tenant) => tenant._id === search.tenantId)?.name;

  // Top-level aggregates over the in-scope (optionally tenant-filtered) data.
  const summaryCards = [
    {
      label: t('recordingAnalytics.avgPerStep'),
      value: formatMinutes(t, summary?.avgPerStepMs ?? 0),
      context: t('recordingAnalytics.acrossSteps', {
        count: summary?.recordingCount ?? 0
      })
    },
    {
      label: t('recordingAnalytics.avgPerProcess'),
      value: formatMinutes(t, summary?.avgPerProcessMs ?? 0),
      context: t('recordingAnalytics.acrossProcesses', {
        count: summary?.processCount ?? 0
      })
    }
  ];

  const tenantColumns = useMemo<DataTableColumnDef<RecordingTenantRow>[]>(
    () => [
      {
        accessorKey: 'tenantName',
        header: createColumnHeader(t('recordingAnalytics.tenant')),
        cell: ({ row }) => (
          <button
            className="text-left font-medium hover:underline"
            onClick={() => setTenant(row.original.tenantId)}
          >
            {row.original.tenantName}
          </button>
        )
      },
      {
        accessorKey: 'processCount',
        header: createColumnHeader(t('recordingAnalytics.processCount'))
      },
      {
        accessorKey: 'stepCount',
        header: createColumnHeader(t('recordingAnalytics.stepCount'))
      },
      {
        accessorKey: 'avgPerStepMs',
        header: createColumnHeader(t('recordingAnalytics.avgPerStep')),
        cell: ({ row }) => formatMinutes(t, row.original.avgPerStepMs)
      },
      {
        accessorKey: 'avgPerProcessMs',
        header: createColumnHeader(t('recordingAnalytics.avgPerProcess')),
        cell: ({ row }) => formatMinutes(t, row.original.avgPerProcessMs)
      },
      {
        accessorKey: 'totalActiveRecordingMs',
        header: createColumnHeader(t('recordingAnalytics.total')),
        cell: ({ row }) => formatMinutes(t, row.original.totalActiveRecordingMs)
      }
    ],
    [t, setTenant]
  );

  const processColumns = useMemo<DataTableColumnDef<RecordingProcessRow>[]>(
    () => [
      {
        id: 'expander',
        meta: { fitContent: true },
        header: () => null,
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={row.getToggleExpandedHandler()}
              className="h-6 w-6 cursor-pointer"
            >
              <ChevronRight
                className={`h-4 w-4 transition-transform ${row.getIsExpanded() ? 'rotate-90' : ''}`}
              />
            </Button>
          ) : null,
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'processName',
        header: createColumnHeader(t('recordingAnalytics.process'))
      },
      {
        accessorKey: 'stepCount',
        header: createColumnHeader(t('recordingAnalytics.stepCount'))
      },
      {
        accessorKey: 'totalActiveRecordingMs',
        header: createColumnHeader(t('recordingAnalytics.total')),
        cell: ({ row }) => formatMinutes(t, row.original.totalActiveRecordingMs)
      },
      {
        accessorKey: 'avgPerStepMs',
        header: createColumnHeader(t('recordingAnalytics.avgPerStep')),
        cell: ({ row }) => formatMinutes(t, row.original.avgPerStepMs)
      }
    ],
    [t]
  );

  return (
    <Page>
      <PageContent>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{t('recordingAnalytics.title')}</h1>
          <p className="text-muted-foreground">{t('recordingAnalytics.description')}</p>
        </div>

        {/* Tenant scope selector — cleared state shows the per-tenant overview */}
        <div className="mb-4">
          <Label>{t('recordingAnalytics.scope')}</Label>
          <Select value={search.tenantId ?? ''} onValueChange={(v) => setTenant(v || undefined)}>
            <SelectTrigger
              allowClear
              hasValue={!!search.tenantId}
              onClear={() => setTenant(undefined)}
            >
              <SelectValue placeholder={t('recordingAnalytics.allTenants')} />
            </SelectTrigger>
            <SelectContent>
              {realTenants?.map((tenant) => (
                <SelectItem key={tenant._id} value={tenant._id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Dim stale keepPreviousData numbers while the new scope loads. */}
        <div
          className={`mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 ${
            isPlaceholderData ? 'opacity-50' : ''
          }`}
        >
          {summaryCards.map((card) => (
            <SummaryCard key={card.label} {...card} isLoading={isLoading} />
          ))}
        </div>

        {search.tenantId ? (
          <>
            {selectedTenantName && (
              <h2 className="mb-3 text-lg font-semibold">{selectedTenantName}</h2>
            )}
            <DataTable
              columns={processColumns}
              data={processes}
              isLoading={isLoading || isPlaceholderData}
              enableSorting
              enableExpanding
              getRowCanExpand={(row) => row.original.steps.length > 0}
              renderSubComponent={({ row }) => <StepsSubTable steps={row.original.steps} />}
              tableId="recordingAnalyticsProcesses"
              emptyMessage={t('recordingAnalytics.empty')}
            />
          </>
        ) : (
          <DataTable
            columns={tenantColumns}
            data={tenants}
            isLoading={isLoading || isPlaceholderData}
            enableSorting
            tableId="recordingAnalyticsTenants"
            emptyMessage={t('recordingAnalytics.empty')}
          />
        )}
      </PageContent>
    </Page>
  );
}
